import React, { useEffect, useState } from 'react';
import axios from 'axios';
import './wallet.css';

// The cards at the top of /payments (owner, 2026-09-07): what card money came
// in today / this week / this month, what of it went back, the prepaid money
// sitting in client wallets, the gateway's last-7-day picture — and, in one
// row, everything on the page that needs a person: each chip is a queue with
// rows in it, and clicking it scrolls there. Queues with nothing in them no
// longer render at all, so this row is the only place they are mentioned.
//
//   GET /api/wallet/payments-overview -> { today: {count, amount},
//        week: {count, amount}, month: {count, amount, refunded},
//        wallets: {balance, funded} }
//
// `attention` and `gateway` come from the parent, which hears the counts from
// the sections themselves — one source of truth for "is this queue empty".
const money = (n) =>
    `AED ${Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const plural = (n, word) => `${n} ${word}${n === 1 ? '' : 's'}`;

// Order = how urgent: stranded/held money first, routine approvals last.
const QUEUES = [
    { key: 'held',      label: 'Held for review',     target: 'sec-held',      tone: 'red' },
    { key: 'issues',    label: 'Payment issues',      target: 'sec-issues',    tone: 'red' },
    { key: 'recon',     label: 'Reconciliation',      target: 'sec-issues',    tone: 'red' },
    { key: 'belowZero', label: 'Clients below zero',  target: 'sec-belowzero', tone: 'red' },
    { key: 'deposits',  label: 'Deposits to approve', target: 'sec-deposits',  tone: 'orange' },
    { key: 'payouts',   label: 'Payouts to pay',      target: 'sec-payouts',   tone: 'orange' },
];

export default function PaymentsOverview({ attention = {}, gateway = null }) {
    const [data, setData] = useState(null);
    const [failed, setFailed] = useState(false);

    useEffect(() => {
        let alive = true;
        axios.get('/api/wallet/payments-overview')
            .then((res) => { if (alive) setData(res.data || null); })
            .catch((err) => {
                if (err.response?.status !== 404) console.error('Error loading payments overview:', err);
                if (alive) setFailed(true);
            });
        return () => { alive = false; };
    }, []);

    const jump = (id) => {
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };

    const open = QUEUES.filter((q) => Number(attention[q.key] || 0) > 0);
    const gw = gateway && Array.isArray(gateway.checkouts) ? gateway.checkouts : [];
    const gwPick = (status) => gw.find((r) => String(r.status).toLowerCase() === status);
    const gwPaid = gwPick('paid');
    const gwFailed = gwPick('failed');
    const gwExpired = gwPick('expired');

    return (
        <div className="swallet__overview">
            {!failed && (
                <div className="swallet__kpis">
                    <div className="swallet__kpi">
                        <div className="swallet__kpiLabel">Collected today</div>
                        <div className="swallet__kpiValue">{data ? money(data.today.amount) : '…'}</div>
                        <div className="swallet__kpiSub">{data ? plural(data.today.count, 'card payment') : ''}</div>
                    </div>
                    <div className="swallet__kpi">
                        <div className="swallet__kpiLabel">Last 7 days</div>
                        <div className="swallet__kpiValue">{data ? money(data.week.amount) : '…'}</div>
                        <div className="swallet__kpiSub">{data ? plural(data.week.count, 'card payment') : ''}</div>
                    </div>
                    <div className="swallet__kpi">
                        <div className="swallet__kpiLabel">This month</div>
                        <div className="swallet__kpiValue">{data ? money(data.month.amount) : '…'}</div>
                        <div className="swallet__kpiSub">
                            {data ? (
                                <>
                                    {plural(data.month.count, 'card payment')}
                                    {data.month.refunded > 0 && ` · ${money(data.month.refunded)} refunded`}
                                </>
                            ) : ''}
                        </div>
                    </div>
                    <div className="swallet__kpi">
                        <div className="swallet__kpiLabel">Client wallet balances</div>
                        <div className="swallet__kpiValue">{data ? money(data.wallets.balance) : '…'}</div>
                        <div className="swallet__kpiSub">
                            {data ? `prepaid, across ${plural(data.wallets.funded, 'funded wallet')}` : ''}
                        </div>
                    </div>
                    {gateway && (
                        <div className="swallet__kpi swallet__kpi--recon">
                            <div className="swallet__kpiLabel">Gateway · last {gateway.days || 7} days</div>
                            <div className="swallet__gwRow">
                                <span className="swallet__gwItem">
                                    <b>{gwPaid ? gwPaid.count : 0}</b> paid{gwPaid ? ` · ${money(gwPaid.total)}` : ''}
                                </span>
                                <span className="swallet__gwItem">
                                    <b>{gwFailed ? gwFailed.count : 0}</b> failed
                                </span>
                                <span className="swallet__gwItem">
                                    <b>{gwExpired ? gwExpired.count : 0}</b> expired
                                </span>
                            </div>
                            <div className="swallet__kpiSub">
                                {gateway.issues > 0
                                    ? `${plural(gateway.issues, 'row')} to work in Reconciliation`
                                    : 'nothing to reconcile'}
                            </div>
                        </div>
                    )}
                </div>
            )}

            <div className="swallet__attention">
                <span className="swallet__attentionLabel">Needs attention</span>
                {open.length === 0 ? (
                    <span className="swallet__attnClear">Nothing — every queue is empty</span>
                ) : open.map((q) => (
                    <button
                        key={q.key}
                        type="button"
                        className={`swallet__attnChip swallet__attnChip--${q.tone}`}
                        onClick={() => jump(q.target)}
                        title="Scroll to this queue"
                    >
                        {q.label}
                        <span className="swallet__attnCount">{attention[q.key]}</span>
                    </button>
                ))}
            </div>
        </div>
    );
}
